// src/plugins/registry.ts  (MODIFIED by reviewradar-v5-20k-builder)
//
// Auto-discovery registry. Vite's import.meta.glob walks src/plugins/<id>/manifest.json
// at build time — drop a folder in, it's found. No manual imports anywhere.
//
// Builder delta: in addition to the static glob, we merge in user-built plugins
// from `src/plugins/builder/dynamicRegistry`. The shape is identical, so every
// downstream caller (loader, admin page, marketplace) Just Works.
import { getDynamicEntries, getDynamicManifests } from './builder/dynamicRegistry';
// Vite: eager-load manifests (JSON), lazy-load modules (code).
// The explicit { query: '?json', import: 'default' } tells Vite to inline each JSON.
const manifestModules = import.meta.glob('/src/plugins/*/manifest.json', { eager: true, import: 'default' });
const uiModules = import.meta.glob('/src/plugins/*/plugin.tsx');
const hookModules = import.meta.glob('/src/plugins/*/hooks.ts');
function idFromPath(path) {
    // "/src/plugins/auto-reply/manifest.json" -> "auto-reply"
    const m = path.match(/\/src\/plugins\/([^/]+)\//);
    return m ? m[1] : path;
}
// Must be declared before loadRegistry references it — see __setRegistryForTests below.
let _override = null;
/** Returns the full discovered registry, indexed by plugin id.
 *  When the test override is active it short-circuits dynamic merging too. */
export function loadRegistry() {
    if (_override)
        return _override;
    const out = {};
    for (const [path, manifest] of Object.entries(manifestModules)) {
        const id = idFromPath(path);
        const uiPath = `/src/plugins/${id}/plugin.tsx`;
        const hookPath = `/src/plugins/${id}/hooks.ts`;
        const entry = { manifest };
        const uiLoader = uiModules[uiPath];
        const hookLoader = hookModules[hookPath];
        if (uiLoader)
            entry.loadComponent = uiLoader;
        if (hookLoader)
            entry.loadHandlers = hookLoader;
        out[manifest.id || id] = entry;
    }
    // Merge user-built plugins from the in-browser builder store. Static wins
    // on id collision (defensive — `user.` prefix should make this impossible).
    for (const [id, entry] of Object.entries(getDynamicEntries())) {
        if (!out[id])
            out[id] = entry;
    }
    return out;
}
/** Returns just the manifests — fast, sync, safe to call anywhere. */
export function listManifests() {
    if (_override)
        return Object.values(_override).map(e => e.manifest);
    return [...Object.values(manifestModules), ...getDynamicManifests()];
}
/** Testing hook — inject a fake registry for unit tests. */
export function __setRegistryForTests(reg) { _override = reg; }
export function __getRegistryForTests() { return _override; }
/** Resolve a single plugin by id, honoring the test override if present. */
export function getEntry(id) {
    if (_override)
        return _override[id];
    return loadRegistry()[id];
}
